import { atom, useAtom, useSetAtom } from 'jotai/index';
import { confirmDialog } from 'primereact/confirmdialog';
import { InputTextarea } from 'primereact/inputtextarea';
import React, { useState } from 'react';
import {
    BodySmallRegular,
    H2Regular,
    useToastController,
    YStack,
} from 'smoothie';
import { useDebouncedCallback } from 'use-debounce';

import { Button } from '../../../common/components/GroveButtons';
import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import { requestBuilder } from '../../../http/RequestBuilder';
import { TrackedEvent } from '../../../logger/Events';
import { captureException, trackEvent } from '../../../logger/Logger';

export const isBulkMessageModalOpenAtom = atom(false);

export const BulkMessageModal = () => {
    const [isBulkMessageModalOpen, setIsBulkMessageModalOpen] = useAtom(
        isBulkMessageModalOpenAtom,
    );
    const closeModal = () => {
        setIsBulkMessageModalOpen(false);
    };

    return (
        <GroveSimpleModal
            isOpen={isBulkMessageModalOpen}
            hasCloseButton={true}
            onClose={closeModal}
            renderInAppRoot={false}
        >
            <YStack
                marginHorizontal={16}
                marginTop={32}
                width="90vw"
                maxWidth={600}
            >
                <H2Regular>Bulk Message</H2Regular>
                <BodySmallRegular marginVertical={16}>
                    Send a message to all of your active memberships.
                </BodySmallRegular>
                <BulkMessageWidget />
            </YStack>
        </GroveSimpleModal>
    );
};

const useBulkSendMessage = requestBuilder.mutation<
    unknown,
    { message: string }
>({
    endpoint: '/api/admin/guide-messaging/send/bulk',
    method: 'POST',
});

const BulkMessageWidget = () => {
    const toast = useToastController();
    const setIsBulkMessageModalOpen = useSetAtom(isBulkMessageModalOpenAtom);
    const [message, setMessage] = useState('');
    const { mutate: sendBulkMessage } = useBulkSendMessage();

    const sendBulkMessageCallback = useDebouncedCallback(() => {
        sendBulkMessage(
            {
                body: {
                    message: message.trim(),
                },
            },
            {
                onSuccess: () => {
                    toast.show(
                        'Message sent successfully.',
                        MutationSuccessToastOptions,
                    );
                    setIsBulkMessageModalOpen(false);
                },
                onError: (e) => {
                    toast.show(
                        'Error sending bulk message. Reach out to engineering for support.',
                        MutationErrorToastOptions(),
                    );
                    captureException(e);
                },
            },
        );

        trackEvent(TrackedEvent.BulkMessageSent);
    }, 1000);

    return (
        <YStack gap="2vh">
            <InputTextarea
                autoResize
                style={{ width: '100%', height: 40 }}
                value={message}
                onChange={(e) => {
                    setMessage(e.target.value);
                }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
            />
            <Button
                disabled={message.trim().length === 0}
                onPress={() => {
                    confirmDialog({
                        header: 'Bulk Send Message',
                        message:
                            'Are you sure you want to bulk message all of your active memberships?',
                        acceptLabel: 'Send',
                        rejectLabel: 'Cancel',
                        accept: () => {
                            sendBulkMessageCallback();
                        },
                        visible: true,
                    });
                }}
            >
                Send
            </Button>
        </YStack>
    );
};
